import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { syncManager } from '../lib/syncManager';
import { onSignOut } from '../lib/signOutReset';
import { SubjectRank } from '../constants/intellectualSpheres';
import { IntellectualPathName } from '../constants/intellectualPaths';

export interface IntellectualSubject {
  id: string;
  user_id: string;
  subject_name: string;
  description: string | null;
  /** The Subject's primary Knowledge Path (IN2; legacy nulls = "General"). */
  primary_sphere: IntellectualPathName | null;
  secondary_spheres: IntellectualPathName[];
  level: number;
  xp: number;
  rank: SubjectRank;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface SubjectActivity {
  id: string;
  user_id: string;
  subject_id: string;
  xp_earned: number;
  source_type: 'session' | 'project' | 'routine' | 'resource' | 'manual';
  source_id: string | null;
  notes: string | null;
  created_at: string;
}

interface IntellectualSubjectsState {
  subjects: IntellectualSubject[];
  activities: SubjectActivity[];
  isLoading: boolean;
  error: string | null;

  fetchSubjects: () => Promise<void>;
  addSubject: (subject: {
    subject_name: string;
    description?: string;
    primary_sphere?: IntellectualPathName | null;
    secondary_spheres?: IntellectualPathName[];
  }) => Promise<IntellectualSubject | null>;
  updateSubject: (id: string, updates: Partial<{
    subject_name: string;
    description: string | null;
    primary_sphere: IntellectualPathName | null;
    secondary_spheres: IntellectualPathName[];
    is_active: boolean;
  }>) => Promise<void>;
  deleteSubject: (id: string) => Promise<void>;
  addXPToSubject: (
    subjectId: string,
    xpAmount: number,
    sourceType: 'session' | 'project' | 'routine' | 'resource' | 'manual',
    sourceId?: string,
    notes?: string
  ) => Promise<void>;
  getSubject: (id: string) => IntellectualSubject | undefined;
  getActiveSubjects: () => IntellectualSubject[];
  getSubjectsBySphere: (sphere: IntellectualPathName | null) => IntellectualSubject[];
  getGeneralKnowledgeSubjects: () => IntellectualSubject[];
  clearStore: () => void;
}

/**
 * ⚠️⚠️ MOVED BY `clearStore` (sign-out). A load or a write notes it before its first
 * await and drops its answer when it has moved: work still in flight for the account
 * that signed out must not land in the next account's empty store.
 */
let epoch = 0;

/** An action's failure — shown only while the account it ran for is still signed in. */
function failed(at: number, error: unknown) {
  if (at === epoch) useIntellectualSubjectsStore.setState({ error: (error as Error).message });
}

export const useIntellectualSubjectsStore = create<IntellectualSubjectsState>((set, get) => ({
  subjects: [],
  activities: [],
  isLoading: false,
  error: null,

  fetchSubjects: async () => {
    const at = epoch;
    set({ isLoading: true, error: null });
    try {
      const { user } = await syncManager.safeAuthCheck();
      if (!user) {
        set({ isLoading: false });
        return;
      }

      const { data, error } = await supabase
        .from('intellectual_subjects')
        .select('*')
        .eq('user_id', user.id)
        .order('subject_name');
      // Signed out while it read: the next account must not get this one's subjects.
      if (at !== epoch) return;

      if (error) throw error;

      set({ subjects: (data || []) as IntellectualSubject[], isLoading: false });
    } catch (error) {
      if (at === epoch) set({ error: (error as Error).message, isLoading: false });
    }
  },

  addSubject: async (subject) => {
    const at = epoch;
    try {
      const { user } = await syncManager.safeAuthCheck();
      if (!user) return null;

      const { data, error } = await supabase
        .from('intellectual_subjects')
        .insert({
          user_id: user.id,
          subject_name: subject.subject_name,
          description: subject.description || null,
          primary_sphere: subject.primary_sphere || null,
          secondary_spheres: subject.secondary_spheres || []
        })
        .select()
        .single();

      if (error) throw error;

      const newSubject = data as IntellectualSubject;
      // Saved for the account that signed out meanwhile — not shown to the next one.
      if (at !== epoch) return newSubject;
      set(state => ({ subjects: [...state.subjects, newSubject].sort((a, b) => a.subject_name.localeCompare(b.subject_name)) }));
      return newSubject;
    } catch (error) {
      failed(at, error);
      return null;
    }
  },

  updateSubject: async (id, updates) => {
    const at = epoch;
    try {
      const { error } = await supabase
        .from('intellectual_subjects')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id);

      if (error) throw error;

      set(state => ({
        subjects: state.subjects.map(s =>
          s.id === id ? { ...s, ...updates, updated_at: new Date().toISOString() } : s
        )
      }));
    } catch (error) {
      failed(at, error);
    }
  },

  deleteSubject: async (id) => {
    const at = epoch;
    try {
      const { error } = await supabase
        .from('intellectual_subjects')
        .delete()
        .eq('id', id);

      if (error) throw error;

      set(state => ({
        subjects: state.subjects.filter(s => s.id !== id)
      }));
    } catch (error) {
      failed(at, error);
    }
  },

  addXPToSubject: async (subjectId, xpAmount, sourceType, sourceId, notes) => {
    const at = epoch;
    try {
      const { user } = await syncManager.safeAuthCheck();
      if (!user) return;

      const { error: activityError } = await supabase
        .from('intellectual_subject_activities')
        .insert({
          user_id: user.id,
          subject_id: subjectId,
          xp_earned: xpAmount,
          source_type: sourceType,
          source_id: sourceId || null,
          notes: notes || null
        });

      if (activityError) throw activityError;

      const subject = get().subjects.find(s => s.id === subjectId);
      if (subject) {
        const newXP = subject.xp + xpAmount;

        const { error: updateError } = await supabase
          .from('intellectual_subjects')
          .update({ xp: newXP })
          .eq('id', subjectId);

        if (updateError) throw updateError;

        // NOTE (IN2): the legacy per-sphere ledger fan-out is removed — a Path's
        // level now DERIVES from the aggregate XP of its Subjects (IN1 Overview),
        // so we only persist Subject XP here. The global Intellectual domain
        // credit (useUserStats) is wired in IN3. The `intellectual_spheres`
        // table is orphaned, scheduled for removal in IN4.
      }

      await get().fetchSubjects();
    } catch (error) {
      failed(at, error);
    }
  },

  getSubject: (id) => {
    return get().subjects.find(s => s.id === id);
  },

  getActiveSubjects: () => {
    return get().subjects.filter(s => s.is_active);
  },

  getSubjectsBySphere: (sphere) => {
    if (sphere === null) {
      return get().subjects.filter(s => s.primary_sphere === null);
    }
    return get().subjects.filter(
      s => s.primary_sphere === sphere || s.secondary_spheres.includes(sphere)
    );
  },

  getGeneralKnowledgeSubjects: () => {
    return get().subjects.filter(s => s.primary_sphere === null);
  },

  /**
   * Back to empty — run on sign-out (lib/signOutReset). No `persist`, but a sign-out does
   * not reload the page. An update or delete still in flight maps by id over the emptied
   * list, so it changes nothing here.
   */
  clearStore: () => {
    epoch += 1;
    set({ subjects: [], activities: [], isLoading: false, error: null });
  },
}));

onSignOut(() => useIntellectualSubjectsStore.getState().clearStore());
